import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Loader2, CreditCard } from "lucide-react";
import { api, formatCHF, formatDate, formatTime } from "@/lib/api";
import { PaymentChoice } from "@/components/PaymentChoice";
import { GiftCodeField, remiseCadeau } from "@/components/GiftCodeField";
import { LieuActions, LieuMap } from "@/components/LieuActions";
import { AApporter } from "@/components/AApporter";
import { ModePaiement, ACOMPTE } from "@/components/ModePaiement";
import { LA_A_APPORTER } from "@/data/lieuxAnciens";
import { WyrdGate } from "@/components/WyrdGate";
import { useNavigate } from "react-router-dom";
import { stageBookingPath } from "@/data/stagePricing";

const PEAUX = ["Autruche", "Biche", "Bison", "Buffle", "Cerf", "Chamois", "Chèvre", "Chèvre blanche", "Chèvre colorée", "Daim", "Lama", "Sanglier", "Veau", "Yak"];
const DIAMETRES = ["40", "45", "50"];

const inputCls = "w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra";

export const BookingDialog = ({ event, open, onOpenChange }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", date_naissance: "", participants: 1, paiement: "en_ligne", mode_paiement: "total", message: "", peau: "Chèvre", diametre: "45", location_tambour: false, choix_tambour: "tambour_elm", });
  const [loading, setLoading] = useState(false);
  const [membre, setMembre] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);
  const [carte, setCarte] = useState(null);

  const eventId = event?.id;
  const eventType = event?.type;
  useEffect(() => {
    if (eventType === "cercle") {
      setForm((f) => f.paiement === "facture" ? { ...f, paiement: "en_ligne" } : f);
    }
  }, [eventId, eventType]);
  useEffect(() => {
    if (open && eventId && eventType === "stage") { onOpenChange(false); navigate(stageBookingPath(eventId)); }
  }, [open, eventId, eventType, onOpenChange, navigate]);

  if (!event || event.type === "stage") return null;
  const isLieuAncien = event.type === "lieu_ancien";
  const isRandonnee = event.type === "randonnee";
  const isSoin = event.type === "soin";
  const isCercle = event.type === "cercle";
  const isDebutants = !!event.cercle_debutants;
  const lieuSecret = /secret/i.test(event.lieu || "") || (isRandonnee && event.rdv_prive !== false);
  const isStage = event.type === "stage" || isLieuAncien;
  const wyrd = !!event.reserve_parcours;
  const locPrix = event.location_tambour_prix || 0;
  const prixDebutants = { tambour_elm: 0, tambour_autre: 30, location: 50 };
  const payant = isDebutants ? prixDebutants[form.choix_tambour] > 0 : !wyrd && event.prix != null && event.prix > 0;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form, event_id: event.id, participants: isSoin || isDebutants ? 1 : Number(form.participants), paiement: !payant ? "gratuit" : isSoin || isCercle ? "en_ligne" : form.paiement, origin_url: window.location.origin, code_cadeau: carte?.code || null };
      if (!isSoin) delete payload.date_naissance;
      if (isStage) {
        payload.forme = "Ronde";
      } else {
        delete payload.peau;
        delete payload.diametre;
      }
      const res = await api.book(payload);
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      setConfirmation(res);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue, merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setConfirmation(null);
      setError(null);
      setMembre(null);
    }
  };
  const totalBrut = isDebutants ? prixDebutants[form.choix_tambour] : wyrd ? 0 : event.prix != null ? ((event.prix + (form.location_tambour ? locPrix : 0)) * Number(form.participants || 1)) : null;
  const total = isDebutants ? totalBrut : membre && totalBrut != null ? (isCercle ? (form.location_tambour ? locPrix : 0) : Math.round(totalBrut * 90) / 100) : totalBrut;
  const remise = total ? remiseCadeau(carte, total) : 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="!flex !flex-col z-[60] bg-sand border-wood/20 w-[calc(100%-1rem)] sm:w-full max-w-lg max-h-[calc(100dvh-1rem)] sm:max-h-[90dvh] overflow-y-auto overflow-x-hidden overscroll-contain gap-4 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-6 [&>*]:min-w-0 [&>*]:shrink-0" data-testid="booking-dialog">
        {confirmation ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 text-sage mx-auto mb-4" />
            <DialogHeader className="pr-8">
              <DialogTitle className="font-serif text-2xl text-bark">{isSoin ? "Vous participez au soin" : "Votre place est réservée"}</DialogTitle>
              <DialogDescription className="text-fumee">
                Référence <span className="font-mono text-terra">{confirmation.reference}</span> — un courriel de confirmation
                arrive vers {confirmation.email}.{isSoin ? " Ludovic vous contactera par WhatsApp avec les consignes d'activation." : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-6 bg-sand-dark/60 rounded-xl p-5 text-sm text-left space-y-2">
              <p className="font-medium text-bark">{confirmation.event_titre}</p>
              {confirmation.choix_tambour_label && <p className="text-fumee" data-testid="booking-confirmation-drum-choice">{confirmation.choix_tambour_label}</p>}
              {confirmation.peau && (
                <p className="text-fumee" data-testid="booking-confirmation-choix">
                  Votre tambour : peau de {confirmation.peau.toLowerCase()} · forme ronde {confirmation.diametre} cm
                </p>
              )}
              <p className="text-fumee">
                {confirmation.participants} place(s) · {confirmation.wyrd_membre ? "compris dans votre parcours du Wyrd" : confirmation.montant > 0
                  ? `${formatCHF(confirmation.montant)} · ${confirmation.paiement === "carte_cadeau" ? "réglé par carte cadeau" : "règlement par facture sous 10 jours"}`
                  : event.prix_label ? event.prix_label.toLowerCase() : event.type === "cercle" ? "tarif communiqué par Ludovic" : "tarif communiqué directement par Ludovic"}
              </p>
            </div>
            <button
              onClick={() => close(false)}
              data-testid="booking-success-close"
              className="mt-8 px-8 py-3 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="pr-8">
              <DialogTitle className="font-serif text-2xl text-bark">{event.titre}</DialogTitle>
              <DialogDescription className="text-fumee">
                {event.date_label || `${formatDate(event.date)} · ${formatTime(event.date)}`} · {event.lieu}
              </DialogDescription>
            </DialogHeader>
            {!isSoin && !lieuSecret && (!isLieuAncien || event.lieu_confirme) && <LieuMap lieu={event.coordonnees || event.lieu} />}
            {isSoin && (
              <div className="rounded-xl border border-terra/30 bg-terra/5 p-4 text-xs text-bark leading-relaxed" data-testid="booking-soin-infos">
                Soin collectif réalisé à distance, à activer chez vous pendant 7 jours. Une inscription par personne. Après votre inscription, Ludovic vous contacte par WhatsApp au numéro indiqué avec les consignes.
              </div>
            )}
            {isRandonnee && (
              <div className="rounded-xl border border-sage/40 bg-sage-light/40 p-4 text-xs text-bark leading-relaxed space-y-1" data-testid="booking-randonnee-infos">
                <p>{event.rdv_prive === false && event.point_rdv ? "Randonnée-expérience de terrain en petit groupe. Le point de rendez-vous est publié sur cette fiche et visible sur la carte." : "Randonnée-expérience de terrain en petit groupe. Le lieu de rendez-vous précis vous est communiqué quelques jours avant."}</p>
                {event.difficulte_marche && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Marche</span>{event.difficulte_marche}</p>}
                {event.equipement && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Équipement</span>{event.equipement}</p>}
                {event.meteo && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Météo</span>{event.meteo}</p>}
              </div>
            )}
            {isLieuAncien && (
              <div className="rounded-xl border border-terra/30 bg-terra/5 p-4 text-xs text-bark leading-relaxed space-y-1" data-testid="booking-lieu-ancien-infos">
                {event.lieu_confirme ? (
                  <>
                    {event.acces && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Accès</span>{event.acces}</p>}
                    {event.difficulte_marche && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Marche</span>{event.difficulte_marche}</p>}
                    {event.equipement && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Équipement</span>{event.equipement}</p>}
                    {event.meteo && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra mr-2">Météo</span>{event.meteo}</p>}
                  </>
                ) : (
                  <p>Journée complète en plein air. Le lieu précis, le point de rendez-vous, l'accès et l'équipement conseillé vous sont communiqués avant la journée. En cas de météo défavorable, la journée est reportée.</p>
                )}
              </div>
            )}
            {!isSoin && !lieuSecret && <LieuActions event={event} />}
            {isStage && <AApporter testid="booking-a-apporter" items={isLieuAncien ? LA_A_APPORTER : undefined} titre={isLieuAncien ? "À apporter pour la journée" : undefined} />}
            {wyrd && !membre && <WyrdGate onVerified={(email, r) => { setMembre(r); setForm((f) => ({ ...f, email, participants: 1, paiement: "facture" })); }} />}
            {!wyrd && !isDebutants && payant && !membre && <WyrdGate benefit onVerified={(email, r) => { setMembre(r); setForm((f) => ({ ...f, email, participants: isCercle ? 1 : f.participants })); }} />}
            {wyrd && membre && (
              <p className="rounded-xl border border-sage/40 bg-sage/10 px-4 py-3 text-sm text-bark" data-testid="wyrd-membre-ok">
                Membre du Wyrd confirmé{membre.prenom ? ` — ${membre.prenom}` : ""} · référence <span className="font-mono text-terra">{membre.reference}</span>. Cette séance est comprise dans votre parcours.
              </p>
            )}
            {!wyrd && !isDebutants && membre && (
              <p className="rounded-xl border border-sage/40 bg-sage/10 px-4 py-3 text-sm text-bark" data-testid="wyrd-benefit-ok">
                Avantage Wyrd confirmé{membre.prenom ? ` — ${membre.prenom}` : ""}.{isCercle ? " Votre place au cercle est offerte." : " La remise de 10 % sera appliquée à la confirmation."}
              </p>
            )}
            {(!wyrd || membre) && <form onSubmit={submit} className="space-y-4 mt-2 min-w-0 shrink-0" data-testid="booking-form">
              <div className="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-4 [&>*]:min-w-0">
                <div className="col-span-full">
                  <label htmlFor="bk-nom" className="eyebrow block mb-2">Nom complet</label>
                  <input id="bk-nom" data-testid="booking-nom" required value={form.nom} onChange={set("nom")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="bk-email" className="eyebrow block mb-2">Courriel</label>
                  <input id="bk-email" data-testid="booking-email" type="email" required readOnly={wyrd || !!membre} value={form.email} onChange={set("email")} className={`${inputCls} ${wyrd || membre ? "opacity-70" : ""}`} />
                </div>
                <div>
                  <label htmlFor="bk-tel" className="eyebrow block mb-2">Téléphone</label>
                  <input id="bk-tel" data-testid="booking-telephone" type="tel" required minLength={6} value={form.telephone} onChange={set("telephone")} className={inputCls} />
                </div>
                {isSoin ? (
                  <div className={payant ? "" : "col-span-full"}>
                    <label htmlFor="bk-naissance" className="eyebrow block mb-2">Date de naissance</label>
                    <input id="bk-naissance" data-testid="booking-date-naissance" type="date" required max={new Date().toISOString().slice(0, 10)} value={form.date_naissance} onChange={set("date_naissance")} className={inputCls} />
                  </div>
                ) : (
                <div className={payant ? "" : "col-span-full"}>
                  <label htmlFor="bk-places" className="eyebrow block mb-2">Places</label>
                  <select id="bk-places" data-testid="booking-participants" value={form.participants} onChange={set("participants")} disabled={wyrd || isDebutants || (isCercle && !!membre)} className={inputCls}>
                    {Array.from({ length: wyrd || isDebutants || (isCercle && membre) ? 1 : event.places_illimitees ? 10 : Math.min(event.places_restantes, 4) }).map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                )}
                {isDebutants && <div className="col-span-full space-y-2" data-testid="booking-beginner-drum-choice"><span className="eyebrow block mb-2">Votre tambour</span>{[["tambour_elm", "Je possède un tambour Entre Les Mondes", "Participation offerte · CHF 0.–"], ["tambour_autre", "Je possède un tambour d’une autre provenance", "Je viens avec mon tambour · CHF 30.–"], ["location", "Je souhaite louer un tambour", `Participation CHF 30.– + location CHF 20.– · CHF 50.–${event.locations_tambours_restantes != null ? ` · ${event.locations_tambours_restantes} disponible(s)` : ""}`]].map(([value, label, detail]) => { const unavailable = value === "location" && Number(event.locations_tambours_restantes) < 1; return <label key={value} className={`flex gap-3 rounded-xl border p-4 transition-colors ${form.choix_tambour === value ? "border-terra bg-terra/10" : "border-wood/25"} ${unavailable ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-terra/60"}`}><input type="radio" name="choix-tambour" value={value} checked={form.choix_tambour === value} disabled={unavailable} onChange={() => setForm((f) => ({ ...f, choix_tambour: value, location_tambour: value === "location" }))} data-testid={`booking-drum-choice-${value}`} className="mt-1 accent-[#AE9A64]" /><span className="text-sm text-bark"><strong className="block font-medium">{label}</strong><span className="text-xs text-fumee">{unavailable ? "Location complète — apportez votre propre tambour" : detail}</span></span></label>; })}</div>}
                {!isDebutants && locPrix > 0 && (
                  <label className="col-span-full flex items-start gap-3 rounded-xl border border-wood/20 px-4 py-3 cursor-pointer hover:border-wood/40 transition-colors" data-testid="booking-location-label">
                    <input type="checkbox" data-testid="booking-location-tambour" checked={form.location_tambour} onChange={(e) => setForm((f) => ({ ...f, location_tambour: e.target.checked }))} className="mt-1 accent-[#AE9A64]" />
                    <span className="text-sm text-bark">Je n'ai pas de tambour — location sur place <span className="text-terra">+ {formatCHF(locPrix)} / personne</span>
                      <span className="block text-xs text-fumee mt-0.5">Sans location : {formatCHF(event.prix)} avec votre propre tambour.</span></span>
                  </label>
                )}
                {isStage && (
                  <>
                    <div className="col-span-full border-t border-sand-dark pt-4">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Votre tambour — forme ronde</p>
                    </div>
                    <div>
                      <label htmlFor="bk-peau" className="eyebrow block mb-2">Type de peau</label>
                      <select id="bk-peau" data-testid="booking-peau" value={form.peau} onChange={set("peau")} className={inputCls}>
                        {PEAUX.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="bk-diametre" className="eyebrow block mb-2">Diamètre</label>
                      <select id="bk-diametre" data-testid="booking-diametre" value={form.diametre} onChange={set("diametre")} className={inputCls}>
                        {DIAMETRES.map((d) => <option key={d} value={d}>{d} cm</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div className="col-span-full">
                  <label htmlFor="bk-msg" className="eyebrow block mb-2">{isSoin ? "Votre intention (optionnel)" : "Message (optionnel)"}</label>
                  <textarea id="bk-msg" data-testid="booking-message" rows={2} value={form.message} onChange={set("message")}
                    className={`${inputCls} resize-none`} />
                </div>
                {payant && !isSoin && (
                  <div className="col-span-full" data-testid="booking-payment-section">
                    <span className="eyebrow block mb-2">Paiement</span>
                    <PaymentChoice compact allowInvoice={!isCercle} value={form.paiement} onChange={(v) => setForm((f) => ({ ...f, paiement: v }))} testidPrefix="booking" />
                  </div>
                )}
                {payant && isSoin && (
                  <div className="col-span-full" data-testid="booking-payment-section">
                    <span className="eyebrow block mb-2">Paiement</span>
                    <p className="rounded-xl border border-terra/40 bg-terra/5 px-4 py-3 text-xs text-bark leading-relaxed flex items-start gap-2" data-testid="booking-soin-paiement"><CreditCard className="w-4 h-4 text-terra mt-0.5 shrink-0" /><span>TWINT ou carte, en ligne au moment de l'inscription — paiement sécurisé Stripe.</span></p>
                  </div>
                )}
                {payant && <div className="col-span-full"><GiftCodeField carte={carte} onChange={setCarte} testidPrefix="booking" /></div>}
                {payant && isStage && form.paiement === "en_ligne" && (
                  <div className="col-span-full">
                    <span className="eyebrow block mb-2">Régler</span>
                    <ModePaiement value={form.mode_paiement} onChange={(v) => setForm((f) => ({ ...f, mode_paiement: v }))} total={total - remise} testidPrefix="booking" />
                  </div>
                )}
              </div>
              {error && <p data-testid="booking-error" className="text-sm text-destructive">{error}</p>}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pt-2">
                <span className="font-serif text-xl text-bark" data-testid="booking-total">
                  {wyrd ? <span className="text-base text-sage">Compris dans le parcours</span> : total != null ? formatCHF(total - remise) : "Tarif à confirmer"}
                  {membre && !wyrd && !isCercle && !isDebutants && totalBrut != null && <span className="block text-[11px] text-sage font-sans" data-testid="booking-wyrd-discount">avantage Wyrd − {formatCHF(totalBrut - total)}</span>}
                  {membre && isCercle && !isDebutants && <span className="block text-[11px] text-sage font-sans" data-testid="booking-wyrd-circle">place de cercle offerte{form.location_tambour ? " · location en supplément" : ""}</span>}
                  {remise > 0 && <span className="block text-[11px] text-terra font-sans">dont carte cadeau − {formatCHF(remise)}</span>}
                  {isStage && form.paiement === "en_ligne" && form.mode_paiement === "acompte" && total - remise > ACOMPTE && (
                    <span className="block text-[11px] text-terra font-sans" data-testid="booking-acompte-info">aujourd'hui : {formatCHF(ACOMPTE)} · solde {formatCHF(total - remise - ACOMPTE)}</span>
                  )}
                </span>
                <button type="submit" data-testid="booking-submit" disabled={loading}
                  className="w-full sm:w-auto min-h-12 justify-center px-7 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-earth transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? "Un instant…" : wyrd ? "Confirmer ma place" : payant && (form.paiement === "en_ligne" || isSoin) && total - remise > 0 ? (isSoin ? "Participer et payer (TWINT / carte)" : "Réserver et payer (TWINT / carte)") : isSoin ? "Confirmer ma participation" : "Confirmer la réservation"}
                </button>
              </div>
            </form>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
